import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Sparkles, Users, PackageCheck, MapPin, Check, UserCog } from "lucide-react";
import { Btn } from "@/components/site/Btn";
import { Reveal, Bubbles, WaveDivider } from "@/components/site/Motion";
import { HERO, STRIP, STRIP_LINE, TWO_WAYS, WHY_FLOW, EMPLOYMENT, CATEGORIES, MIGRATION_TEASER, FINAL_CTA, WHY_COST, PRICING, ACCOUNT_MANAGER } from "@/lib/content";
import { ART } from "@/lib/assets";

const accent = { sky: "bg-sky", coral: "bg-coral", seagreen: "bg-seagreen", yellow: "bg-yellow" };
const accentSoft = { sky: "bg-sky/15", coral: "bg-coral/15", seagreen: "bg-seagreen/15", yellow: "bg-yellow/25" };

const Hero = () => (
  <section className="relative overflow-hidden bg-gradient-to-b from-sky/30 via-cream to-cream pt-14 md:pt-20 pb-4" data-testid="hero">
    <Bubbles count={10} />
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center relative">
      <div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 bg-white border-2 border-navy rounded-full px-4 py-1.5 shadow-brutal mb-6">
          <Sparkles className="w-4 h-4 text-coral" />
          <span className="font-body font-bold text-xs uppercase tracking-[0.15em]">{HERO.eyebrow}</span>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tight text-navy leading-[0.95] text-5xl md:text-6xl lg:text-7xl">
          {HERO.lines.map((line, i) => (
            <span key={i} className={`flex flex-wrap gap-x-[0.28em] ${i === 2 ? "text-ocean" : ""}`}>
              {line.split(" ").map((word, w) => (
                <span key={w} className="inline-block overflow-hidden pb-[0.08em]">
                  <motion.span className="inline-block" initial={{ y: "110%" }} animate={{ y: 0 }}
                    transition={{ duration: 0.55, delay: 0.1 + i * 0.1 + w * 0.03, ease: [0.22, 1, 0.36, 1] }}>
                    {word}
                  </motion.span>
                </span>
              ))}
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45, duration: 0.5 }}
          className="font-body text-lg md:text-xl text-navy/70 mt-6 max-w-xl leading-relaxed">
          {HERO.sub}
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.5 }}
          className="font-body text-base md:text-lg text-navy/60 mt-4 max-w-xl leading-relaxed">
          {HERO.subIt}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.5 }}
          className="flex flex-wrap gap-4 mt-8">
          <Btn to={HERO.primary.to} variant="yellow" testid="hero-primary-cta">{HERO.primary.label} <ArrowRight className="w-5 h-5" /></Btn>
          <Btn to={HERO.secondary.to} variant="white" testid="hero-secondary-cta">{HERO.secondary.label}</Btn>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="relative">
        <div className="absolute -top-6 -left-6 w-28 h-28 bg-yellow blob-1 anim-float-slow -z-0" aria-hidden="true" />
        <div className="absolute -bottom-8 -right-4 w-24 h-24 bg-coral blob-2 anim-float -z-0" aria-hidden="true" />
        <div className="relative anim-swim">
          <img src={ART.hero} alt="A friendly shark swimming through a colourful ocean of laptops, headsets, spreadsheets, chat bubbles and code" className="w-full rounded-[36px] border-2 border-navy shadow-brutal-lg bg-white" loading="eager" />
        </div>
      </motion.div>
    </div>
    <WaveDivider color="#082F49" className="mt-10" />
  </section>
);

const Strip = () => (
  <section className="bg-navy py-6 md:py-7 border-y-2 border-navy" data-testid="confidence-strip">
    <Marquee speed={45} gradient={false} autoFill>
      {STRIP.map((w, i) => (
        <span key={i} className="flex items-center font-display font-extrabold text-3xl md:text-5xl text-cream mx-6">
          {w}<span className="text-yellow mx-6 text-2xl md:text-4xl">✳</span>
        </span>
      ))}
      <span className="font-display font-medium text-2xl md:text-3xl text-sky mx-10">{STRIP_LINE}</span>
      <span className="text-yellow mx-6 text-2xl md:text-4xl">✳</span>
    </Marquee>
  </section>
);

const TwoWays = () => (
  <section className="py-20 md:py-28" data-testid="two-ways">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal><h2 className="font-display font-bold text-4xl md:text-5xl text-navy text-center">{TWO_WAYS.headline}</h2></Reveal>
      <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-12 items-stretch">
        {[TWO_WAYS.build, TWO_WAYS.project].map((c, i) => (
          <Reveal key={i} delay={i * 0.12}>
            <div className={`h-full ${i === 0 ? "bg-sky/15 border-sky" : "bg-yellow/25 border-yellow"} border-2 rounded-3xl p-8 md:p-10 flex flex-col`}>
              <div className="flex items-center gap-3">
                <span className={`w-12 h-12 grid place-items-center rounded-full bg-white border-2 border-navy`}>{i === 0 ? <Users className="w-6 h-6" /> : <PackageCheck className="w-6 h-6" />}</span>
                <span className="font-body text-xs font-bold uppercase tracking-[0.15em] text-navy/70">{c.tag}</span>
              </div>
              <h3 className="font-display font-extrabold text-2xl md:text-3xl text-navy mt-6">{c.hook}</h3>
              <p className="font-body text-navy/75 mt-3 leading-relaxed flex-1">{c.body}</p>
              <div className="mt-7"><Btn to={c.cta.to} variant={i === 0 ? "sky" : "white"} testid={`two-ways-cta-${i}`}>{c.cta.label} <ArrowRight className="w-5 h-5" /></Btn></div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyFlow = () => (
  <section className="py-20 md:py-28 bg-navy" data-testid="why-flow">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal><h2 className="font-display font-bold text-4xl md:text-5xl text-cream max-w-2xl leading-[1.05]">{WHY_FLOW.headline}</h2></Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {WHY_FLOW.steps.map((s, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div className="flex items-center gap-4 bg-cream border-2 border-navy rounded-2xl px-5 py-4 shadow-brutal-sky h-full">
              <span className="shrink-0 w-9 h-9 grid place-items-center rounded-full bg-yellow border-2 border-navy font-display font-extrabold text-sm">{i + 1}</span>
              <span className="font-display font-bold text-navy text-lg leading-tight">{s}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const EmploymentSection = () => (
  <section className="py-20 md:py-28" data-testid="employment-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <img src={ART.employmentModel} alt="How the employment model works: your company, the professional and BleuSharks support" className="w-full rounded-[32px] border-2 border-navy shadow-brutal-lg bg-white" loading="lazy" />
      </Reveal>
      <Reveal delay={0.12}>
        <h2 className="font-display font-bold text-3xl md:text-5xl text-navy leading-[1.05]">{EMPLOYMENT.headline}</h2>
        <p className="font-body text-lg text-navy/75 mt-5">{EMPLOYMENT.body}</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-7">
          {EMPLOYMENT.columns.map((c, i) => (
            <div key={i} className={`${[accentSoft.sky, accentSoft.yellow, accentSoft.seagreen][i]} border-2 border-navy rounded-2xl p-4`}>
              <p className="font-display font-bold text-navy">{c.title}</p>
              <p className="font-body text-sm text-navy/70 mt-1">{c.body}</p>
            </div>
          ))}
        </div>
        <p className="font-display font-bold text-xl text-navy mt-6 bg-yellow inline-block px-4 py-2 rounded-xl border-2 border-navy shadow-brutal -rotate-1">{EMPLOYMENT.manage}</p>
        <p className="font-body text-navy/75 mt-5">{EMPLOYMENT.pay}</p>
        <p className="font-body text-navy/75 mt-3">{EMPLOYMENT.scale}</p>
      </Reveal>
    </div>
  </section>
);

const Categories = () => (
  <section className="py-20 md:py-28 bg-sky/10" data-testid="categories">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-3">The professionals</p>
        <h2 className="font-display font-bold text-4xl md:text-5xl text-navy max-w-2xl leading-[1.05]">Four kinds of help you can build your team around.</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 gap-6 mt-12">
        {CATEGORIES.map((c, i) => {
          const Icon = c.icon;
          return (
            <Reveal key={c.key} delay={i * 0.08}>
              <div className={`h-full ${accentSoft[c.color]} border-2 border-navy rounded-3xl p-7 shadow-brutal hover:-translate-y-2 hover:rotate-1 transition-transform duration-200`}>
                <span className={`inline-grid place-items-center w-14 h-14 rounded-2xl ${accent[c.color]} border-2 border-navy`}><Icon className="w-7 h-7 text-navy" /></span>
                <h3 className="font-display font-bold text-2xl text-navy mt-5">{c.title}</h3>
                <p className="font-body text-navy/70 mt-2">{c.blurb}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {c.roles.slice(0, 3).map((r) => (<span key={r} className="bg-white border-2 border-navy/20 rounded-full px-3 py-1 font-body text-xs font-semibold">{r}</span>))}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.1}><div className="mt-10"><Btn to="/professionals" variant="sky" testid="categories-cta">Explore the professionals <ArrowRight className="w-5 h-5" /></Btn></div></Reveal>
    </div>
  </section>
);

const MigrationTeaser = () => (
  <section className="py-20 md:py-28" data-testid="migration-teaser">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal delay={0.1} className="lg:order-2">
        <img src={ART.dataMigration} alt="Messy legacy data being cleaned, validated and organised into a modern system" className="w-full rounded-[32px] border-2 border-navy shadow-brutal-lg bg-white anim-float-slow" loading="lazy" />
      </Reveal>
      <Reveal>
        <span className="inline-flex items-center gap-2 bg-seagreen/25 border-2 border-navy rounded-full px-4 py-1.5 font-body font-bold text-xs uppercase tracking-[0.15em] shadow-brutal">Flagship IT project</span>
        <h2 className="font-display font-bold text-4xl md:text-5xl text-navy mt-5 leading-[1.05]">{MIGRATION_TEASER.headline}</h2>
        <p className="font-body text-lg text-navy/75 mt-4">{MIGRATION_TEASER.body}</p>
        <div className="flex flex-wrap items-center gap-2 mt-7">
          {MIGRATION_TEASER.journey.map((j, i) => (
            <span key={j} className="flex items-center gap-2">
              <span className="bg-white border-2 border-navy rounded-full px-4 py-1.5 font-body font-semibold text-sm">{j}</span>
              {i < MIGRATION_TEASER.journey.length - 1 && <ArrowRight className="w-4 h-4 text-ocean" />}
            </span>
          ))}
        </div>
        <div className="mt-8"><Btn to={MIGRATION_TEASER.cta.to} variant="yellow" testid="migration-teaser-cta">{MIGRATION_TEASER.cta.label} <ArrowRight className="w-5 h-5" /></Btn></div>
      </Reveal>
    </div>
  </section>
);

const WhyCost = () => (
  <section className="py-20 md:py-28 bg-seagreen/10" data-testid="why-cost">
    <div className="max-w-4xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-4">The economics</p>
        <h2 className="font-display font-bold text-4xl md:text-5xl text-navy leading-[1.05]">{WHY_COST.headline}</h2>
        <p className="font-display font-bold text-2xl text-navy mt-6 flex items-start gap-3"><MapPin className="w-7 h-7 text-coral shrink-0 mt-1" /> {WHY_COST.lead}</p>
        <div className="mt-5 space-y-4 max-w-2xl">
          {WHY_COST.body.map((p, i) => (<p key={i} className="font-body text-lg text-navy/75 leading-relaxed">{p}</p>))}
        </div>
      </Reveal>
    </div>
  </section>
);

const Pricing = () => (
  <section id="pricing" className="py-20 md:py-28 scroll-mt-24" data-testid="pricing">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-3">Pricing</p>
        <h2 className="font-display font-bold text-4xl md:text-5xl text-navy leading-[1.05]">{PRICING.headline}</h2>
        <p className="font-body text-lg text-navy/70 mt-4">{PRICING.sub}</p>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-10">
        {PRICING.roles.map((r, i) => (
          <Reveal key={r.role} delay={i * 0.1}>
            <div className="bg-white border-2 border-navy rounded-3xl p-8 shadow-brutal h-full" data-testid={`pricing-card-${i}`}>
              <h3 className="font-display font-bold text-xl md:text-2xl text-navy">{r.role}</h3>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="rounded-2xl border-2 border-navy/15 bg-cream p-5">
                  <p className="font-body text-xs font-bold uppercase tracking-[0.12em] text-navy/50">Typical UK in-house cost</p>
                  <p className="font-display font-bold text-2xl md:text-3xl text-navy/60 mt-2">{r.uk}</p>
                </div>
                <div className="rounded-2xl border-2 border-navy bg-yellow p-5 shadow-brutal-sky">
                  <p className="font-body text-xs font-bold uppercase tracking-[0.12em] text-navy/70">With BleuSharks</p>
                  <p className="font-display font-extrabold text-2xl md:text-3xl text-navy mt-2">{r.bs}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-8 space-y-2 max-w-3xl">
          {PRICING.notes.map((n, i) => (<p key={i} className="font-body text-navy/65 flex gap-2"><Check className="w-5 h-5 text-seagreen shrink-0 mt-0.5" /> {n}</p>))}
        </div>
        <div className="mt-8"><Btn to={PRICING.cta.to} variant="yellow" testid="pricing-cta">{PRICING.cta.label} <ArrowRight className="w-5 h-5" /></Btn></div>
      </Reveal>
    </div>
  </section>
);

const AccountManager = () => (
  <section className="py-20 md:py-28 bg-sky/10" data-testid="account-manager">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[auto_1fr] gap-8 items-center">
      <Reveal>
        <span className="inline-grid place-items-center w-20 h-20 rounded-2xl bg-coral border-2 border-navy shadow-brutal"><UserCog className="w-10 h-10 text-navy" /></span>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-navy leading-[1.05]">{ACCOUNT_MANAGER.headline}</h2>
        <p className="font-body text-lg text-navy/75 mt-4 max-w-2xl">{ACCOUNT_MANAGER.body}</p>
        <div className="flex flex-wrap items-center gap-4 mt-5">
          <p className="font-display font-bold text-lg text-navy bg-yellow inline-block px-4 py-2 rounded-xl border-2 border-navy shadow-brutal -rotate-1">{ACCOUNT_MANAGER.highlight}</p>
          <span className="font-body font-semibold text-navy/70">{ACCOUNT_MANAGER.note}</span>
        </div>
      </Reveal>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="relative bg-ocean overflow-hidden" data-testid="final-cta">
    <div className="bg-cream"><WaveDivider color="#0284C7" /></div>
    <Bubbles count={10} />
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center relative">
      <Reveal>
        <img src={ART.handProject} alt="BleuSharks taking on and delivering a project" className="w-56 md:w-72 mx-auto rounded-[32px] border-2 border-navy shadow-brutal-lg bg-white anim-swim" loading="lazy" />
        <h2 className="font-display font-extrabold text-4xl md:text-6xl text-cream mt-10 leading-[1]">{FINAL_CTA.headline}</h2>
        <p className="font-body text-lg md:text-xl text-cream/85 mt-5 max-w-2xl mx-auto">{FINAL_CTA.sub}</p>
        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <Btn to={FINAL_CTA.primary.to} variant="yellow" testid="final-primary-cta">{FINAL_CTA.primary.label} <ArrowRight className="w-5 h-5" /></Btn>
          <Btn to={FINAL_CTA.secondary.to} variant="white" testid="final-secondary-cta">{FINAL_CTA.secondary.label}</Btn>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Strip />
      <TwoWays />
      <WhyFlow />
      <EmploymentSection />
      <WhyCost />
      <Pricing />
      <AccountManager />
      <Categories />
      <MigrationTeaser />
      <FinalCTA />
    </>
  );
}
